import React, { useState, useEffect, useCallback } from 'react';

import PropTypes from 'prop-types';

import { useCurrentProject } from '@adsk/alloy-provider';
import { Flex } from '@adsk/alloy-react-box';
import { ISpeechBubbleIcon, DocumentTextIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import BlockRemoveButton from '../../BlockRemoveButton';
import UnstyledAnchor from '../../UnstyledAnchor';
import { isBuildPro } from './helpers/formatEntities';
import { isRfiUpload, getThumbnail } from './helpers/utils';

const DocsEntityBlock = ({
  projectId,
  sourceEntityType,
  entity,
  onRemoveClick,
  onClick,
  onReferenceClickAnalytics,
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const currentProject = useCurrentProject();
  const [thumbnail, setThumbnail] = useState('');

  const scopes = currentProject?.scopes?.project;
  const {
    urn,
    parentFolderUrn,
    version: { bubbleUrn, bubbleViewableGuid } = {},
  } = entity;
  const url = `/${
    isBuildPro() ? 'build' : 'docs'
  }/files/projects/${projectId}?entityId=${urn}&folderUrn=${parentFolderUrn}&moduleId=folders`;
  const showRfiUpload = isRfiUpload(sourceEntityType, entity);

  const loadThumbnail = useCallback(async () => {
    if (!bubbleUrn) return;
    const thumbnail = await getThumbnail(bubbleUrn, bubbleViewableGuid, scopes);
    if (thumbnail) setThumbnail(thumbnail);
  }, [bubbleUrn, bubbleViewableGuid, scopes]);

  useEffect(() => {
    !thumbnail && loadThumbnail();
    return () => {
      thumbnail && URL.revokeObjectURL(thumbnail);
    };
  }, [thumbnail, loadThumbnail]);

  const handleClick = () => {
    onReferenceClickAnalytics?.();
    onClick?.(entity);
  };

  return (
    <UnstyledAnchor
      style={[
        {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flex: 1,
          overflow: 'hidden',
          padding: 10,
          borderBottom: `1px solid ${theme.colors.charcoal100}`,
          cursor: 'pointer',
          ...(onRemoveClick && {
            paddingRight: 0,
            ['&:hover .DocsEntityBlock__remove-button']: {
              visibility: 'visible',
            },
          }),
        },
      ]}
      href={!onClick && url}
      onClick={handleClick}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 4,
          border: `1px solid ${theme.colors.charcoal200}`,
          width: 48,
          height: 48,
          marginRight: 16,
        }}
      >
        {thumbnail ? (
          <img style={{ width: '100%' }} src={thumbnail} alt="" />
        ) : (
          <DocumentTextIcon size={24} style={{ margin: '0 2px' }} />
        )}
      </div>
      <Flex flexDirection="column" flexGrow={1}>
        <OverflowTooltip
          getTargetProps={() => ({ style: { overflow: 'hidden', flex: 1 } })}
          content={entity.name}
        >
          {entity.name}
        </OverflowTooltip>
        {showRfiUpload && (
          <div style={{ width: '20px' }}>
            <Tooltip content={T.translate('RFI_DIRECT_UPLOAD_TOOLTIP')}>
              <ISpeechBubbleIcon size={20} />
            </Tooltip>
          </div>
        )}
      </Flex>
      {onRemoveClick && (
        <Flex
          height="100%"
          alignItems="center"
          justifyContent="center"
          padding="0 15px 0px 0px"
          marginLeft="4px"
        >
          <BlockRemoveButton
            className="DocsEntityBlock__remove-button"
            style={{
              visibility: 'hidden',
            }}
            onClick={() => onRemoveClick(projectId, entity.urn)}
          />
        </Flex>
      )}
    </UnstyledAnchor>
  );
};

DocsEntityBlock.propTypes = {
  projectId: PropTypes.string,
  sourceEntityType: PropTypes.string,
  entity: PropTypes.shape({
    id: PropTypes.string,
    urn: PropTypes.string,
    parentFolderUrn: PropTypes.string,
    parentFolder: PropTypes.object,
    name: PropTypes.node,
    version: PropTypes.object,
  }),
  onRemoveClick: PropTypes.func,
  onClick: PropTypes.func,
  onReferenceClickAnalytics: PropTypes.func,
};

export default DocsEntityBlock;
